import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import AdminHeader from '@/components/feature/AdminHeader';
import LoadingState from '@/components/base/LoadingState';
import ErrorState from '@/components/base/ErrorState';
import { supabase } from '@/lib/supabaseClient';
import { getTenant } from '@/lib/getTenant';
import DetailModal from './components/DetailModal';

/* ─── Types ──────────────────────────────────────────────────── */
export interface EmailQueueItem {
  id: string;
  to_email: string;
  subject: string;
  body: string | null;
  status: string;
  template_type: string | null;
  entity_type: string | null;
  entity_id: string | null;
  priority: number;
  scheduled_for: string | null;
  sent_at: string | null;
  created_at: string;
  retry_count: number;
  last_error: string | null;
  metadata: Record<string, unknown> | null;
}

type StatusFilter = 'all' | 'pending' | 'processing' | 'sent' | 'failed';

/* ─── Constants ──────────────────────────────────────────────── */
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all',        label: 'All' },
  { value: 'pending',    label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'sent',       label: 'Sent' },
  { value: 'failed',     label: 'Failed' },
];

const STATUS_BADGE: Record<string, string> = {
  pending:    'bg-amber-100 text-amber-700 border-amber-200',
  processing: 'bg-blue-100 text-blue-700 border-blue-200',
  sent:       'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed:     'bg-rose-100 text-rose-700 border-rose-200',
};

const STATUS_DOT: Record<string, string> = {
  pending:    'bg-amber-500',
  processing: 'bg-blue-500',
  sent:       'bg-emerald-500',
  failed:     'bg-rose-500',
};

const PRIORITY_LABEL: Record<number, { label: string; style: string }> = {
  1: { label: 'High',   style: 'bg-rose-50 text-rose-600 border-rose-100' },
  2: { label: 'Medium', style: 'bg-amber-50 text-amber-600 border-amber-100' },
  3: { label: 'Low',    style: 'bg-slate-50 text-slate-500 border-slate-100' },
};

/* ─── Helpers ────────────────────────────────────────────────── */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateShort(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function toDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/* ─── Component ──────────────────────────────────────────────── */
export default function EmailQueuePage() {
  const [emails, setEmails] = useState<EmailQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [detailEmail, setDetailEmail] = useState<EmailQueueItem | null>(null);

  /* ── Fetch ── */
  const fetchEmails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tenant = await getTenant();
      if (!tenant) {
        setError('No tenant context found. Please log in again.');
        setLoading(false);
        return;
      }

      const { data, err } = await supabase
        .from('email_queue')
        .select('*')
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: false })
        .limit(500);

      if (err) {
        setError(err.message ?? 'Failed to load email queue');
        setEmails([]);
      } else if (data) {
        setEmails(data as EmailQueueItem[]);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  /* ── Derived KPIs ── */
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayISO = today.toISOString();

  const kpis = {
    pending:    emails.filter((e) => e.status === 'pending').length,
    sent:       emails.filter((e) => e.status === 'sent').length,
    failed:     emails.filter((e) => e.status === 'failed').length,
    processing: emails.filter((e) => e.status === 'processing').length,
    sentToday:  emails.filter((e) => e.status === 'sent' && e.sent_at && e.sent_at >= todayISO).length,
  };

  /* ── Template types (unique from data) ── */
  const templateTypes = Array.from(
    new Set(emails.map((e) => e.template_type).filter(Boolean)),
  ) as string[];

  /* ── Priority values ── */
  const priorityValues = Array.from(
    new Set(emails.map((e) => e.priority)),
  ).sort((a, b) => a - b);

  /* ── Filtered ── */
  const filtered = emails.filter((e) => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (templateFilter !== 'all' && e.template_type !== templateFilter) return false;
    if (priorityFilter !== 'all' && String(e.priority) !== priorityFilter) return false;
    if (dateFrom && e.created_at < dateFrom + 'T00:00:00Z') return false;
    if (dateTo && e.created_at > dateTo + 'T23:59:59Z') return false;
    return true;
  });

  return (
    <AdminLayout>
      <AdminHeader
        title="Email Queue"
        subtitle="Monitor outgoing emails in real-time"
        actions={
          <button
            onClick={fetchEmails}
            className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-refresh-line text-sm"></i>
            </span>
            Refresh
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7 max-w-7xl">

        {/* ── KPI cards ── */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { key: 'pending',    label: 'Pending',    value: kpis.pending,    dot: 'bg-amber-500',   bg: 'bg-amber-50',   border: 'border-amber-100' },
            { key: 'sent',       label: 'Sent',       value: kpis.sent,       dot: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-100' },
            { key: 'failed',     label: 'Failed',     value: kpis.failed,     dot: 'bg-rose-500',    bg: 'bg-rose-50',    border: 'border-rose-100' },
            { key: 'processing', label: 'Processing', value: kpis.processing, dot: 'bg-blue-500',    bg: 'bg-blue-50',    border: 'border-blue-100' },
            { key: 'sentToday',  label: 'Sent Today', value: kpis.sentToday,  dot: 'bg-teal-500',    bg: 'bg-teal-50',    border: 'border-teal-100' },
          ].map((kpi) => (
            <div
              key={kpi.key}
              className={`${kpi.bg} ${kpi.border} border rounded-xl px-4 py-3.5 flex items-center gap-3`}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${kpi.dot} shrink-0`}></span>
              <div>
                <p className="text-2xl font-bold text-slate-800 tabular-nums">{kpi.value}</p>
                <p className="text-[11px] text-slate-500 font-medium">{kpi.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Status filter pills */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  statusFilter === opt.value
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Template type filter */}
          {templateTypes.length > 0 && (
            <select
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 cursor-pointer outline-none focus:border-slate-300"
            >
              <option value="all">All Templates</option>
              {templateTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          )}

          {/* Priority filter */}
          {priorityValues.length > 0 && (
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 cursor-pointer outline-none focus:border-slate-300"
            >
              <option value="all">All Priorities</option>
              {priorityValues.map((p) => (
                <option key={p} value={String(p)}>
                  {PRIORITY_LABEL[p]?.label ?? `Priority ${p}`}
                </option>
              ))}
            </select>
          )}

          {/* Date range filter */}
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 outline-none focus:border-slate-300 w-[140px] cursor-pointer"
                placeholder="From"
              />
              {dateFrom && (
                <button
                  onClick={() => setDateFrom('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-slate-200 hover:bg-slate-300 text-slate-500 text-[10px] cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            <span className="text-xs text-slate-300">\u2014</span>
            <div className="relative">
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 outline-none focus:border-slate-300 w-[140px] cursor-pointer"
                placeholder="To"
              />
              {dateTo && (
                <button
                  onClick={() => setDateTo('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-slate-200 hover:bg-slate-300 text-slate-500 text-[10px] cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>

          {/* Active filters clear */}
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-[10px] text-slate-400 hover:text-slate-600 underline cursor-pointer whitespace-nowrap"
            >
              Clear dates
            </button>
          )}

          {/* Result count */}
          <span className="text-[11px] text-slate-400 ml-auto">
            {filtered.length} of {emails.length} emails
          </span>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState
            title="Failed to load email queue"
            message={error}
            onRetry={fetchEmails}
            theme="dark"
          />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-slate-50">
              <i className="ri-mail-settings-line text-slate-300 text-3xl"></i>
            </div>
            <div className="text-center">
              <h3 className="text-slate-700 font-semibold text-sm mb-1">No emails in queue</h3>
              <p className="text-slate-400 text-xs max-w-xs mx-auto">
                {statusFilter !== 'all' || templateFilter !== 'all' || priorityFilter !== 'all' || dateFrom || dateTo
                  ? 'No emails match the current filters. Try adjusting them.'
                  : 'Outgoing emails will appear here when the system queues them for delivery.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Created</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Recipient</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Subject</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Template</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Entity</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Priority</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Retries</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Sent</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Error</th>
                  <th className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((email) => (
                  <tr key={email.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    {/* Created */}
                    <td className="px-3 py-3">
                      <span className="text-xs text-slate-500 whitespace-nowrap">{formatDateShort(email.created_at)}</span>
                    </td>

                    {/* Recipient */}
                    <td className="px-3 py-3">
                      <span className="text-xs text-slate-600 truncate block max-w-[160px]" title={email.to_email}>
                        {email.to_email}
                      </span>
                    </td>

                    {/* Subject */}
                    <td className="px-3 py-3">
                      <span className="text-xs text-slate-700 font-medium truncate block max-w-[180px]" title={email.subject}>
                        {email.subject || '\u2014'}
                      </span>
                    </td>

                    {/* Template */}
                    <td className="px-3 py-3">
                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        {email.template_type ? email.template_type.replace(/_/g, ' ') : '\u2014'}
                      </span>
                    </td>

                    {/* Entity */}
                    <td className="px-3 py-3">
                      {email.entity_type ? (
                        <div className="flex flex-col">
                          <span className="text-xs text-slate-600 whitespace-nowrap">{email.entity_type.replace(/_/g, ' ')}</span>
                          {email.entity_id && (
                            <span className="text-[10px] text-slate-400 font-mono">{email.entity_id.slice(0, 8)}...</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">\u2014</span>
                      )}
                    </td>

                    {/* Priority */}
                    <td className="px-3 py-3">
                      {email.priority && PRIORITY_LABEL[email.priority] ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${PRIORITY_LABEL[email.priority].style}`}>
                          {PRIORITY_LABEL[email.priority].label}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">\u2014</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${STATUS_BADGE[email.status] ?? STATUS_BADGE.pending}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[email.status] ?? STATUS_DOT.pending}`}></span>
                        {email.status}
                      </span>
                    </td>

                    {/* Retry count */}
                    <td className="px-3 py-3">
                      <span className={`text-xs font-mono tabular-nums ${email.retry_count > 0 ? 'text-amber-600 font-semibold' : 'text-slate-400'}`}>
                        {email.retry_count}
                      </span>
                    </td>

                    {/* Sent */}
                    <td className="px-3 py-3">
                      <span className="text-xs text-slate-500 whitespace-nowrap">{formatDateShort(email.sent_at)}</span>
                    </td>

                    {/* Error */}
                    <td className="px-3 py-3">
                      {email.last_error ? (
                        <span className="text-xs text-rose-500 truncate block max-w-[120px]" title={email.last_error}>
                          {email.last_error.length > 30 ? email.last_error.slice(0, 30) + '...' : email.last_error}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">\u2014</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-3">
                      <button
                        onClick={() => setDetailEmail(email)}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap"
                        title="View details"
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailEmail && (
        <DetailModal email={detailEmail} onClose={() => setDetailEmail(null)} />
      )}
    </AdminLayout>
  );
}